/**
 * web/src/lib/todayMetrics.ts — the client half of card hSgJtaF6deWm
 * ("One-tap Today screen").
 *
 * The card's acceptance criterion is "100% of Today cards complete their
 * core action without navigation; interaction time logged". The server half
 * is POST /api/metrics/today-interaction (worker/src/api/metrics.ts), which
 * emits the `today_card_action` PostHog event. This is the clock.
 *
 * One event per card's FIRST completed core action:
 *   markTodayCardVisible()  — called from each card's mount (its first paint
 *                             in the stream) to start the clock.
 *   logTodayAction()        — called after the action's API call resolves,
 *                             to stop it.
 *
 * `ms_to_act` is "how long the card sat painted before the tap actually
 * completed something" — the number that answers whether one-tap really is
 * one tap, or whether people are hunting for the control.
 *
 * Contract with the tap it measures: this module NEVER throws and NEVER
 * blocks. A failed log is one console line at most, and it is fire-and-forget
 * so a slow analytics write can never stall the action or hold the card in a
 * busy state. An unauthenticated visitor simply logs nothing.
 */
import { api } from './api';

/** The card kinds the server accepts (mirrors TODAY_CARDS in metrics.ts).
 *  A closed union rather than `string` so a typo is a build error instead of
 *  a silently-dropped event — the server rejects anything outside it too. */
export type TodayCardKind = 'request' | 'question' | 'date' | 'nudge';

/** The per-kind core actions (mirrors TODAY_ACTIONS in metrics.ts). */
export type TodayAction = 'accept' | 'decline' | 'answer' | 'raincheck' | 'set_time' | 'quick_pick';

/** Card first-paint times, keyed by kind + stream sequence number. A card
 *  that re-renders keeps its original stamp — the clock starts when the card
 *  appeared, not on every redraw. */
const visibleAt = new Map<string, number>();

const keyOf = (card: TodayCardKind, seq: number): string => `${card}:${seq}`;

/** Start the clock for one card. Idempotent per (card, seq): a re-render of
 *  the same card does not reset it, so a card that sits in the stream for an
 *  hour before the tap reports the real hour, not the last render. */
export function markTodayCardVisible(card: TodayCardKind, seq: number): void {
  const key = keyOf(card, seq);
  if (!visibleAt.has(key)) visibleAt.set(key, Date.now());
  // Bound the map: the stream holds a window of cards, but it scrolls, and an
  // unbounded ledger would leak one entry per card ever painted.
  if (visibleAt.size > 400) {
    const oldest = visibleAt.keys().next().value;
    if (oldest !== undefined) visibleAt.delete(oldest);
  }
}

/** Stop the clock and log the completed action. Fire-and-forget: returns
 *  immediately, never rejects, never awaits the network. */
export function logTodayAction(card: TodayCardKind, seq: number, action: TodayAction): void {
  try {
    const key = keyOf(card, seq);
    let started = visibleAt.get(key);
    if (started === undefined) {
      /* The core action may complete AWAY from the Thread card — the answer
         to today's question is written in Us → Questions now (founder email
         2026-10-21 moved it there behind an answer pill), and that screen
         has no stream seq to address. Fall back to the oldest clock started
         for this card kind: there is exactly one live question card, so the
         stamp is unambiguous, and the journey is still measured from when
         the card first painted rather than from the panel opening. */
      const prefix = `${card}:`;
      for (const [k, v] of visibleAt) {
        if (k.startsWith(prefix)) { started = v; visibleAt.delete(k); break; }
      }
    } else {
      visibleAt.delete(key);
    }
    const msToAct = typeof started === 'number' ? Date.now() - started : null;
    // `void` + a caught promise: the response is irrelevant, and a rejection
    // (offline, 401, a slow worker) must not surface as an unhandled
    // rejection in the console or, worse, inside a React error boundary.
    void api('/api/metrics/today-interaction', { body: { card, action, msToAct } }).catch(() => {
      /* analytics is best-effort by contract */
    });
  } catch {
    /* never let the clock break the tap it is timing */
  }
}
